import { useState, useEffect } from 'react';

import { ApolloError } from '@apollo/client';
import { Button } from '@netfront/ui-library';
import { useRouter } from 'next/router';

import { ReportTable } from '../ReportTable';

import { OVERALL_PDF_TABLE_COLUMNS } from './OverallPdfTableView.constants';
import { getOverallPdfUsageTableData, groupAndSumPdfUsage } from './OverallPdfTableView.helpers';
import { IGroupedPdfUsage, IOverallPdfUsageTableData, OverallPdfTableViewProps } from './OverallPdfTableView.interfaces';

import { useExportPdfReport, useGetPdfUsage, useToast } from '../../../../../hooks';
import { REPORTING_PAGE_CONSTANTS } from '../../ReportingPage.constants';

const { pageSize: defaultPageSize, tableSelectionItems } = REPORTING_PAGE_CONSTANTS;

const OverallPdfTableView = ({ selectedTable, onTableChange, selectedView, onViewChange, dateRange, onDateRangeChange }: OverallPdfTableViewProps) => {
  const { query } = useRouter();
  const { handleToastError } = useToast();

  const { projectId } = query;
  
  const [filter, setFilter] = useState<string>('');
  const [pdfUsages, setPdfUsages] = useState<IGroupedPdfUsage[]>([]);
  const [allPdfUsages, setAllPdfUsages] = useState<IGroupedPdfUsage[]>([]);
  const [pdfUsageTableData, setPdfUsageTableData] = useState<IOverallPdfUsageTableData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isLoadMoreDisabled, setIsLoadMoreDisabled] = useState<boolean>(true);
  const [pageSize, setPageSize] = useState<number>(Number(defaultPageSize));
  const [lastItemIndex, setLastItemIndex] = useState<number>(0);

  const handleApolloError = (error?: ApolloError) => {
    if (!error) {
      return;
    }

    handleToastError({
      error,
      shouldUseFriendlyErrorMessage: true,
    });
  };

  const { handleExportPdfReport, isLoading: isExportUsageReportLoading = false } = useExportPdfReport({
    fetchPolicy: 'no-cache',
    onCompleted: ({ report: { presignedUrl } }) => {
      window.open(String(presignedUrl), '_blank');
    },
    onError: (error) => {
      handleApolloError(error);
    },
  });


  const { handleGetPdfUsage, isLoading: isGetPdfUsageLoading = false } = useGetPdfUsage({
    fetchPolicy: 'no-cache',
    onCompleted: ({ edges }) => {
      
      const formattedPdfUsages = groupAndSumPdfUsage(edges.map((edge) => edge.node));

      setAllPdfUsages(formattedPdfUsages);
      setTotalItems(formattedPdfUsages.length);
      const paginatedItems = formattedPdfUsages.slice(0, pageSize);
      setIsLoadMoreDisabled(paginatedItems.length >= formattedPdfUsages.length || formattedPdfUsages.length <= pageSize);
      setPdfUsages(paginatedItems);
      setLastItemIndex(pageSize);

    },
    onError: (error) => {
      handleApolloError(error);
    },
  });

  const handleChangeSearchValue = (value?: string) => {
    setFilter(value ?? '');
  };

  const handleDownloadReport = () => {
    handleExportPdfReport({
      from: dateRange[0],
      ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      projectId: String(projectId),
      to: dateRange[1],
    });
  };

  const handleGeneratePdfReport = () => {
    handleGetPdfUsage({
      filter,
      from: dateRange[0],
      ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      projectId: String(projectId ?? ''),
      to: dateRange[1],
    });
  };

  const handlePaginate = () => {
    return new Promise<void>((resolve) => {
      const updateIndex = lastItemIndex + pageSize;
      setLastItemIndex(updateIndex);
      setIsLoadMoreDisabled(totalItems <= updateIndex);
      setPdfUsages(allPdfUsages.slice(0, updateIndex));
      resolve();
    });
  };

  const handlePageSizeChange = (changedPageSize: number) => {
    setPageSize(changedPageSize);
    setLastItemIndex(changedPageSize);
    setIsLoadMoreDisabled(totalItems <= changedPageSize);
    setPdfUsages(allPdfUsages.slice(0, changedPageSize));
  };

  useEffect(() => {
    if (selectedTable !== tableSelectionItems[1].value) {
      return;
    }
    
    handleGeneratePdfReport();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, filter, selectedTable]);


  useEffect(() => {
    setPdfUsageTableData(
      getOverallPdfUsageTableData({
        pdfUsages,
      }),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfUsages]);

  const isLoading = isGetPdfUsageLoading || isExportUsageReportLoading;

  return (
    <ReportTable<IOverallPdfUsageTableData> 
      childrenEnd={<Button size="xs" text="Download" type="button" onClick={() => handleDownloadReport()} />}
      columns={OVERALL_PDF_TABLE_COLUMNS}
      data={pdfUsageTableData}
      dateRange={dateRange}
      emptyStateMessage="There are no pdf usage data available yet"
      handleOnPageSizeChange={handlePageSizeChange}
      handleOnPaginate={handlePaginate}
      handleSearch={handleChangeSearchValue}
      isLoading={isLoading}
      isLoadMoreDisabled={isLoadMoreDisabled}
      pageSize={pageSize}
      selectedTable={selectedTable}
      selectedView={selectedView}
      totalItems={totalItems}
      onDateRangeChange={onDateRangeChange}
      onTableChange={onTableChange}
      onViewChange={onViewChange}
    />
  );
};

export { OverallPdfTableView };
